import { useTraduccion } from '@tallerya/i18n';
import { Boton, CampoTelefono, CampoTexto, Fila, Formulario, Seccion, useAvisos, useFormulario } from '@tallerya/ui';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useTituloPantalla } from '../../layout/titulo';
import { EliminarSucursal } from './EliminarSucursal';
import { useCambiosSucursal, useFichaSucursal, version, type FichaSucursal, type ValoresSucursal } from './datos';

// Alta y edición de una sucursal: «Sucursales - Nueva sucursal» y «Editar
// sucursal» del Figma. La misma pantalla para las dos, porque los campos son los
// mismos; cambia el título, qué botones hay y a qué endpoint va.
//
//   /sucursales/nueva   alta
//   /sucursales/:id     edición: se pide la ficha, con la versión para guardar
//
// La ficha se pide aunque se venga del listado, que ya tenía la fila: la
// edición tiene que partir de la versión que está en la base, y quien llega por
// un enlace o recarga no tiene listado.

export function FormularioSucursal() {
  const { t } = useTraduccion();
  const navegar = useNavigate();
  const { id } = useParams();
  const ficha = useFichaSucursal(id);
  // Aquí y no en el editor: mientras la ficha carga, o si no existe, la barra
  // igual dice dónde se está.
  useTituloPantalla(id === undefined ? t('admin.sucursales.nueva') : t('admin.sucursales.editar.titulo'));

  if (id === undefined) return <Editor />;

  if (ficha.isPending) return <p className="entrada">{t('app.cargando')}</p>;

  if (ficha.isError || ficha.data === null) {
    const noExiste = ficha.data === null;
    return (
      <section className="aviso">
        <h2>{noExiste ? t('admin.sucursales.noExiste.titulo') : t('ui.listado.error.titulo')}</h2>
        <p>{noExiste ? t('admin.sucursales.noExiste.texto') : t('ui.listado.error.texto')}</p>
        <Boton variante="secundario" onClick={() => void navegar('/sucursales')}>
          {t('admin.sucursales.volver')}
        </Boton>
      </section>
    );
  }

  // La clave lleva la versión: si la ficha cambia en la base y se vuelve a pedir,
  // el formulario arranca de nuevo con los valores frescos en vez de conservar
  // los viejos.
  return <Editor key={`${ficha.data.id}-${ficha.data.version}`} ficha={ficha.data} />;
}

function Editor({ ficha }: { ficha?: FichaSucursal }) {
  const { t } = useTraduccion();
  const navegar = useNavigate();
  const { avisar } = useAvisos();
  const cambios = useCambiosSucursal();
  const [eliminando, setEliminando] = useState(false);
  const editando = ficha !== undefined;

  const form = useFormulario<ValoresSucursal>({
    inicial: {
      code: ficha?.code ?? '',
      name: ficha?.name ?? '',
      phone: ficha?.phone ?? '',
      address: ficha?.address ?? '',
    },
    // La cortesía de no ir y volver por lo obvio. La que vale es la de la API.
    validar: (v) => ({
      ...(!v.name.trim() && { name: 'validation.required' }),
      ...(editando && !v.code.trim() && { code: 'validation.required' }),
    }),
    guardar: (v) =>
      ficha ? cambios.editar(String(ficha.id), version(ficha.version), v) : cambios.crear(v),
    // Los dos choques que tienen un campo: se marcan bajo él y no arriba.
    camposPorCodigo: {
      'location.codigo_tomado': 'code',
      'location.nombre_duplicado': 'name',
    },
  });

  function guardado(siguiente: 'volver' | 'otro') {
    const nombre = form.valores.name.trim();
    avisar({
      titulo: editando
        ? t('admin.sucursales.aviso.guardada', { nombre })
        : t('admin.sucursales.aviso.creada', { nombre }),
    });
    if (siguiente === 'otro') {
      form.reiniciar();
      return;
    }
    void navegar('/sucursales');
  }

  const estado = ficha?.isActive ? t('admin.sucursales.enServicio') : t('admin.sucursales.fueraDeServicio');

  return (
    <>
      <Formulario
        form={form}
        titulo={editando ? t('admin.sucursales.editar.titulo') : t('admin.sucursales.nueva')}
        // Al editar, de qué sucursal se trata: «SUC-0002 · Norte · En servicio».
        info={editando ? t('admin.sucursales.editar.info') : t('admin.sucursales.nueva.descripcion')}
        {...(ficha && { descripcion: `${ficha.code} · ${ficha.name} · ${estado}` })}
        {...(editando && { etiquetaGuardar: t('admin.sucursales.formulario.guardarCambios') })}
        guardarYOtro={!editando}
        onGuardado={guardado}
        onCancelar={() => void navegar('/sucursales')}
        // La principal no se elimina: el botón no se ofrece.
        {...(ficha &&
          !ficha.isMain && {
            accionSecundaria: (
              <Boton variante="peligro" onClick={() => setEliminando(true)}>
                {t('admin.sucursales.formulario.eliminar')}
              </Boton>
            ),
          })}
      >
        <Seccion
          titulo={t('admin.sucursales.formulario.seccion')}
          info={t('admin.sucursales.formulario.seccionTexto')}
        >
          <Fila>
            <CampoTexto
              form={form}
              nombre="code"
              etiqueta={t('admin.sucursales.formulario.codigo')}
              obligatorio={editando}
              {...(!editando && { info: t('admin.sucursales.formulario.codigoAyuda') })}
              ancho="corto"
              autoComplete="off"
            />
            <CampoTexto
              form={form}
              nombre="name"
              etiqueta={t('admin.sucursales.formulario.nombre')}
              obligatorio
              placeholder={t('admin.sucursales.formulario.nombreEjemplo')}
            />
            <CampoTelefono
              form={form}
              nombre="phone"
              etiqueta={t('admin.sucursales.formulario.telefono')}
              placeholder="2222-0000"
              ancho="corto"
            />
          </Fila>
          <CampoTexto
            form={form}
            nombre="address"
            etiqueta={t('admin.sucursales.formulario.direccion')}
            placeholder={t('admin.sucursales.formulario.direccionEjemplo')}
            autoComplete="street-address"
            ancho="completo"
          />
        </Seccion>
      </Formulario>

      <EliminarSucursal
        sucursal={eliminando ? (ficha ?? null) : null}
        onCerrar={() => setEliminando(false)}
        onEliminada={() => void navegar('/sucursales')}
      />
    </>
  );
}
